import { ActivityFeedbackItem, ActivityFeedbackRowStatus, ActivityRoboticStatus } from '@/libs/fleet-api/services/robotic';
import { Table, Tag, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import { FC } from 'react';

type Props = {
	feedbackItems: ActivityFeedbackItem[];
	status: ActivityRoboticStatus;
	onViewDetail?: (activityDetailId: number) => void;
	onEdit?: (activityDetailId: number) => void;
	onRetry?: (activityDetailId: number) => void;
};

const feedbackStatusColor = (s?: ActivityFeedbackRowStatus): string => {
	if (s === 'Success') return 'success';
	if (s === 'Failed') return 'error';
	if (s === 'In Progress') return 'processing';
	return 'default';
};

const FeedbackHistoryTable: FC<Props> = ({
	feedbackItems,
	status,
	onViewDetail,
	onEdit,
	onRetry,
}) => {
	const dataSource = feedbackItems.map((item, index) => ({ ...item, key: index }));

	const baseColumns = [
		{
			title: 'Pending Step',
			dataIndex: 'pendingStep',
			render: (v: string | undefined) => v || '-',
		},
		{
			title: 'Pending Field',
			dataIndex: 'pendingField',
			render: (v: string | undefined) => v || '-',
		},
		{
			title: 'Pending Message',
			dataIndex: 'pendingMessage',
			render: (v: string | undefined) =>
				v ? (
					<Tooltip title={v}>
						<Typography.Text
							ellipsis
							style={{ maxWidth: 200, display: 'block' }}
						>
							{v}
						</Typography.Text>
					</Tooltip>
				) : (
					'-'
				),
		},
		{
			title: 'Waktu Mulai',
			dataIndex: 'startedAt',
			render: (v: string | undefined) => (v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '-'),
		},
		{
			title: 'Waktu Selesai',
			dataIndex: 'finishedAt',
			render: (v: string | undefined) => (v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '-'),
		},
		{
			title: 'Status',
			dataIndex: 'status',
			render: (v: ActivityFeedbackRowStatus | undefined) =>
				v ? (
					<Tag color={feedbackStatusColor(v)}>{v}</Tag>
				) : (
					'-'
				),
		},
	];

	const extraColumns =
		status !== 'Waiting'
			? [
					{
						title: 'User',
						dataIndex: 'user',
						render: (v: string | undefined) => v || '-',
					},
					{
						title: 'View Detail',
						align: 'center' as const,
						render: (_: unknown, record: ActivityFeedbackItem) => {
							if (!record.isViewAllowed) {
								return <Typography.Text type='secondary'>Detail</Typography.Text>;
							}
							return (
								<a
									onClick={(e) => {
										e.preventDefault();
										if (record.activityDetailId != null) onViewDetail?.(record.activityDetailId);
									}}
								>
									Detail
								</a>
							);
						},
					},
					{
						title: 'Edit',
						align: 'center' as const,
						render: (_: unknown, record: ActivityFeedbackItem) => {
							const disabled = !record.isEditAllowed || status !== 'Pending';
							return disabled ? (
								<Typography.Text type='secondary'>Edit</Typography.Text>
							) : (
								<a
									onClick={(e) => {
										e.preventDefault();
										if (record.activityDetailId != null) onEdit?.(record.activityDetailId);
									}}
								>
									Edit
								</a>
							);
						},
					},
					{
						title: 'Retry',
						align: 'center' as const,
						render: (_: unknown, record: ActivityFeedbackItem) => {
							const disabled = !record.isRetryAllowed || status !== 'Pending';
							return disabled ? (
								<Typography.Text type='secondary'>Retry</Typography.Text>
							) : (
								<a
									onClick={(e) => {
										e.preventDefault();
										if (record.activityDetailId != null) onRetry?.(record.activityDetailId);
									}}
								>
									Retry
								</a>
							);
						},
					},
					{
						title: 'Notes',
						dataIndex: 'notes',
						render: (v: string | undefined) => v || '-',
					},
				]
			: [];

	const allColumns = [...baseColumns, ...extraColumns];

	return (
		<Table
			bordered
			scroll={{ x: 'max-content' }}
			dataSource={dataSource}
			pagination={false}
			columns={allColumns}
		/>
	);
};

export default FeedbackHistoryTable;
